import React from 'react';
import useHelpers from '../../helpers';
import useModels from '../../models';
import Forbidden from '../../views/screens/Forbidden/Forbidden';
import useComponents from '../../views/components';

/**
 * PermissionGuard - Protects routes based on user permissions
 * @param {Object} props
 * @param {string} props.permissionKey - Single permission key required to access the route (e.g., "dashboard", "configuration.admin_users")
 * @param {string[]} props.anyOf - Array of permission keys, user needs ANY one of them to access (OR logic)
 * @param {boolean} props.requireSuperAdmin - If true, only "Súper Administrador" can access, regardless of permissions.
 *   Mirrors the backend gate on sensitive settings (credentials / auth config), which requires super-admin.
 * @param {React.ReactNode} props.children - The component(s) to render if user has permission
 * @returns {React.ReactNode} - Children if authorized, Forbidden component otherwise
 */
const PermissionGuard = ({ permissionKey, anyOf, requireSuperAdmin, children }) => {
  const { usePermissions } = useHelpers();
  const { hasPermission, hasAnyPermission } = usePermissions();

  // Check if permissions have been loaded
  const { useSelectors } = useModels();
  const { useSelector, useAuthSelectors } = useSelectors();
  const { session } = useAuthSelectors();
  const { permissionsLoaded, rol } = useSelector(session);

  const { useSpinners } = useComponents();
  const { SpinnerLoading } = useSpinners();

  // If permissions haven't been loaded yet, show loading spinner
  if (!permissionsLoaded) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <SpinnerLoading />
      </div>
    );
  }

  // Super-admin only routes: deny anyone whose role isn't "Súper Administrador",
  // even if they somehow hold the permission key.
  if (requireSuperAdmin && rol !== "Súper Administrador") {
    return <Forbidden />;
  }

  let hasAccess = false;

  if (permissionKey) {
    // Single permission check
    hasAccess = hasPermission(permissionKey);
  } else if (anyOf && Array.isArray(anyOf)) {
    // Check if user has ANY of the specified permissions
    hasAccess = hasAnyPermission(anyOf);
  }

  // If no permission granted, show the Forbidden page with layout
  if (!hasAccess) {
    return <Forbidden />;
  }

  // User has permission, render the protected content
  return <>{children}</>;
};

export default PermissionGuard;
